import { Component, OnInit } from '@angular/core';
import { GaragePassageService } from '@services/garage-passage.service';
import { GaragePassage, Status } from '@/models/garage-passage.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { CustomButtonInterface } from '@components/new-table/custom-button-interface';
import { faEnvelope, faEnvelopeOpen, faEye } from '@fortawesome/free-solid-svg-icons';
import { MatDialog } from '@angular/material/dialog';
import { DialogLoadingComponent } from '@components/dialog-loading/dialog-loading.component';
import { GaragePassagesDataSource } from '@components/new-table/data-sources/garage-passages.dataSource';
import { GaragePassagesTableSettings } from '@/table-settings/garage/garage-passages-table-settings';
import { EmailConfirmationDialogComponent } from '@components/email-confirmation-dialog/email-confirmation-dialog.component';
import { GaragePassageDetailDialogComponent } from '@pages/garage-passages/garage-passage-detail-dialog/garage-passage-detail-dialog.component';
import { DialogExtractDataComponent } from '@pages/garage-passages/dialog-extract-data/dialog-extract-data.component';

@Component({
  selector: 'app-garage-passages',
  templateUrl: './garage-passages.component.html',
})
export class GaragePassagesComponent implements OnInit {
  dataSource: GaragePassagesDataSource = new GaragePassagesDataSource(this.garagePassageService);
  reloadTrigger$ = new Subject<any>();
  loading = false;
  extractDataLoading = false;
  displayedColumns = GaragePassagesTableSettings.getColumnsSetting();
  customButtons: CustomButtonInterface[] = [
    {
      btnCustomToolTip: 'Mail da Inviare',
      btnCustomFaIcon: faEnvelope,
      btnCustomColor: 'success',
      btnCustomShowIcon: true,
      btnCustomClickEvent: (element: GaragePassage) => this.setStatusToDaInviare(element),
      btnCustomVisible: (element: GaragePassage) => this.canUpdateStatus(element),
    },
    {
      btnCustomToolTip: 'Mail da non inviare',
      btnCustomFaIcon: faEnvelopeOpen,
      btnCustomColor: 'danger',
      btnCustomShowIcon: true,
      btnCustomClickEvent: (element: GaragePassage) => this.setStatusToDaNonInviare(element),
      btnCustomVisible: (element: GaragePassage) => this.canUpdateStatus(element),
    },
    {
      btnCustomToolTip: 'Visualizza Dettaglio',
      btnCustomFaIcon: faEye,
      btnCustomColor: 'info',
      btnCustomShowIcon: true,
      btnCustomClickEvent: (element: GaragePassage) => this.openDetailDialog(element),
      btnCustomVisible: () => true,
    },
  ];
  protected readonly Status = Status;
  private dialogLoading: any;

  constructor(
    private garagePassageService: GaragePassageService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {}

  canUpdateStatus(passage: GaragePassage): boolean {
    return passage.statoRecord !== 'INVIATO' && passage.statoRecord !== 'ESCLUSO';
  }

  setStatusToDaInviare(passage: GaragePassage) {
    if (!this.canUpdateStatus(passage)) return;

    this.dialogLoading = this.dialog.open(DialogLoadingComponent, {
      data: 'Aggiornamento STATO...',
      disableClose: true,
    });

    this.garagePassageService.updateStatus(passage.id, 'DA_INVIARE').subscribe({
      next: () => {
        this.dialogLoading.close();
        this.snackBar.open('Status aggiornato a DA_INVIARE', 'Close', { duration: 3000 });
        this.reloadTrigger$.next();
      },
      error: (error) => {
        this.dialogLoading.close();
        this.snackBar.open("Errore durante l'aggiornamento dello stato", 'Close', {
          duration: 3000,
        });
      },
    });
  }

  setStatusToDaNonInviare(passage: GaragePassage) {
    if (!this.canUpdateStatus(passage)) return;

    this.dialogLoading = this.dialog.open(DialogLoadingComponent, {
      data: 'Aggiornamento STATO...',
      disableClose: true,
    });

    this.garagePassageService.updateStatus(passage.id, 'DA_NON_INVIARE').subscribe({
      next: () => {
        this.dialogLoading.close();
        this.snackBar.open('Status aggiornato a DA_NON_INVIARE', 'Close', { duration: 3000 });
        this.reloadTrigger$.next();
      },
      error: (error) => {
        this.dialogLoading.close();
        this.snackBar.open("Errore durante l'aggiornamento dello stato", 'Close', {
          duration: 3000,
        });
      },
    });
  }

  sendAllEmails() {
    this.dialogLoading = this.dialog.open(DialogLoadingComponent, {
      data: 'Caricamento anteprima email...',
      disableClose: true,
    });

    this.garagePassageService.getEmailPreview().subscribe({
      next: (preview) => {
        this.dialogLoading.close();

        const dialogRef = this.dialog.open(EmailConfirmationDialogComponent, {
          width: '40vw',
          data: {
            countDaInviareValid: preview.countDaInviareValid,
            countDaInviareInvalid: preview.countDaInviareInvalid,
            countDaNonInviare: preview.countDaNonInviare,
          },
          disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.executeEmailSending();
          }
        });
      },
      error: (error) => {
        this.dialogLoading.close();
        this.snackBar.open("Errore durante il recupero dell'anteprima delle email", 'Close', {
          duration: 3000,
        });
      },
    });
  }

  openDetailDialog(passage: GaragePassage) {
    const dialogRef = this.dialog.open(GaragePassageDetailDialogComponent, {
      width: '80vw',
      data: passage,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.reloadTrigger$.next();
      }
    });
  }

  private executeEmailSending() {
    this.dialogLoading = this.dialog.open(DialogLoadingComponent, {
      data: 'Invio Email in corso...',
      disableClose: true,
    });

    this.garagePassageService.sendEmails().subscribe({
      next: () => {
        this.dialogLoading.close();
        this.snackBar.open('Email inviate con successo', 'Close', { duration: 3000 });
        this.reloadTrigger$.next();
      },
      error: (error) => {
        this.dialogLoading.close();
        this.snackBar.open("Errore durante l'invio delle email", 'Close', { duration: 3000 });
      },
    });
  }

  openExtractDataDialog(): void {
    const dialogRef = this.dialog.open(DialogExtractDataComponent, {
      width: '400px',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.date) {
        this.executeDataExtraction(result.date);
      }
    });
  }

  private executeDataExtraction(dateFrom: string): void {
    this.extractDataLoading = true;
    this.dialogLoading = this.dialog.open(DialogLoadingComponent, {
      data: 'Estrazione dati in corso...',
      disableClose: true,
    });

    this.garagePassageService.reloadData(dateFrom).subscribe({
      next: () => {
        this.dialogLoading.close();
        this.extractDataLoading = false;
        this.snackBar.open('Dati estratti con successo', 'Close', { duration: 3000 });
        this.reloadTrigger$.next();
      },
      error: () => {
        this.dialogLoading.close();
        this.extractDataLoading = false;
        this.snackBar.open("Errore durante l'estrazione dei dati", 'Close', { duration: 3000 });
      },
    });
  }
}
